<script setup lang="ts">
import { computed, ref } from 'vue'
import { NButton, NPopconfirm, NTag, useMessage } from 'naive-ui'
import { RouterLink } from 'vue-router'
import type { LintNote } from '../stores/lint_notes'
import { useLintNotesStore } from '../stores/lint_notes'
import { renderDiff, type DiffSegment } from '../composables/useDiffRender'
import { formatAbsoluteDate, formatDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'

// One reviewable row in /lint. Header (kind tag + page link +
// description + age), targeted-replacement diff, three actions:
// Accept / Edit / Dismiss.
//
// The list endpoint returns `current_page_content` per row (and the
// channel does the same on :updated events) so this card renders the
// diff straight from props — no per-card fetch and no N+1 against
// the show endpoint.
//
// Accept is disabled when the row has no proposed_patch (backfilled
// row that was never edited) — UI tells the admin to Edit first.
// Edit is delegated to the parent via 'edit' event so a single
// LintNoteEditModal can be reused across all rows in the list.

const props = defineProps<{
  note: LintNote
}>()

const emit = defineEmits<{
  edit: [note: LintNote]
}>()

// Right-aligning the popconfirms and capping them at this width (well
// under viewport - margins) keeps them inside a narrow mobile viewport
// without horizontal overflow.
const POPCONFIRM_WIDTH = 260

const store = useLintNotesStore()
const message = useMessage()
const now = useNow()

const acting = ref(false)

const hasPatch = computed(() => !!props.note.proposed_patch)

const diffSegments = computed<DiffSegment[]>(() =>
  hasPatch.value && props.note.proposed_patch
    ? renderDiff(props.note.current_page_content, props.note.proposed_patch)
    : [],
)

const SEGMENT_CLASSES: Record<string, string> = {
  context: 'px-3 py-1 text-white/60 bg-white/[0.02]',
  removed: 'px-3 py-1 text-red-200 bg-red-950/40 border-l-2 border-red-500',
  added: 'px-3 py-1 text-green-200 bg-green-950/40 border-l-2 border-green-500',
}
function segmentClass(kind: string): string {
  return SEGMENT_CLASSES[kind] ?? 'border-l-2 border-yellow-500'
}

async function onAccept() {
  acting.value = true
  try {
    await store.accept(props.note.id)
    message.success('Accept queued.')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to accept')
  } finally {
    acting.value = false
  }
}

async function onDismiss() {
  acting.value = true
  try {
    await store.dismiss(props.note.id)
    message.success('Dismiss queued.')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to dismiss')
  } finally {
    acting.value = false
  }
}

async function onWontFix() {
  acting.value = true
  try {
    await store.wontFix(props.note.id)
    message.success("Won't-fix queued — this finding won't be re-flagged.")
  } catch (e) {
    message.error(e instanceof Error ? e.message : "Failed to mark won't-fix")
  } finally {
    acting.value = false
  }
}

function onEdit() {
  emit('edit', props.note)
}
</script>

<template>
  <article
    class="border border-white/10 rounded p-4 flex flex-col gap-3"
    data-testid="lint-note-card"
    :data-note-id="note.id"
  >
    <header class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1 min-w-0">
        <div class="flex items-center gap-2">
          <NTag size="small" :type="note.kind === 'contradiction' ? 'error' : 'warning'">
            {{ note.kind.replace('_', ' ') }}
          </NTag>
          <RouterLink
            :to="`/p/${note.page_path}`"
            class="text-sm font-mono text-white/80 hover:text-white truncate no-underline"
            >{{ note.page_path }}</RouterLink
          >
          <NTag
            v-if="note.unverified"
            size="small"
            type="info"
            data-testid="lint-unverified"
            title="This finding concerns an external source Lint cannot check. Verify it there before changing the page."
          >
            unverified — check the source
          </NTag>
          <!-- Persistence signal: a finding re-surfaced by N
               later runs is more likely a real, standing problem than a
               one-off. reflag_count is 0 on first flag, so only show the
               badge once it has actually been re-flagged. -->
          <span
            v-if="note.reflag_count > 0"
            data-testid="lint-reflag-count"
            class="text-xs text-amber-300/80 shrink-0 tabular-nums"
            :title="`Re-flagged by ${note.reflag_count} later run(s)`"
            >flagged {{ note.reflag_count + 1 }}×</span
          >
        </div>
        <p class="text-sm text-white m-0">{{ note.description }}</p>
      </div>
      <time
        :datetime="note.created_at"
        :title="formatAbsoluteDate(note.created_at)"
        class="text-xs text-white/40 shrink-0"
        >{{ formatDate(note.created_at, now) }}</time
      >
    </header>

    <!-- Cap the diff height so a full-file patch scrolls within
         the card instead of pushing the Accept/Dismiss footer off
         screen. `overflow-y-auto` still clips child backgrounds to the
         rounded border the way the prior `overflow-hidden` did. -->
    <section
      v-if="hasPatch"
      class="rounded border border-white/10 overflow-y-auto max-h-[24rem] font-mono text-xs"
      data-testid="lint-diff"
    >
      <div
        v-for="(seg, idx) in diffSegments"
        :key="idx"
        :class="segmentClass(seg.kind)"
        data-testid="lint-diff-segment"
        :data-segment-kind="seg.kind"
      >
        <pre v-if="seg.kind !== 'orphan'" class="whitespace-pre-wrap m-0">{{ (seg as { text: string }).text }}</pre>
        <p v-else class="m-0 px-3 py-2 text-yellow-200">
          The page changed since this patch was proposed. Click Edit to regenerate against the current page.
        </p>
      </div>
    </section>

    <p v-else class="text-sm text-white/50 m-0 italic">
      No patch yet — this row was backfilled from an earlier Lint run. Use Edit to generate one.
    </p>

    <footer class="flex justify-end gap-2">
      <!-- `placement="top-end"` + capped `width` keep the popconfirm
           inside a narrow mobile viewport. The trigger sits near the right
           edge of the row, so right-aligning the popover and capping
           its width lets popper position it without horizontal overflow. -->
      <NPopconfirm
        placement="top-end"
        :width="POPCONFIRM_WIDTH"
        positive-text="Won't fix"
        negative-text="Cancel"
        :positive-button-props="{ type: 'error' }"
        @positive-click="onWontFix"
      >
        <template #trigger>
          <NButton :disabled="acting" data-testid="lint-wont-fix">Won't fix</NButton>
        </template>
        Mark this finding as won't-fix? The row closes like a dismiss, but later Lint / ingest runs will NOT re-flag the
        same issue on this page.
      </NPopconfirm>
      <NPopconfirm
        placement="top-end"
        :width="POPCONFIRM_WIDTH"
        positive-text="Dismiss"
        negative-text="Cancel"
        @positive-click="onDismiss"
      >
        <template #trigger>
          <NButton :disabled="acting" data-testid="lint-dismiss">Dismiss</NButton>
        </template>
        Dismiss this finding? The row closes and the inline annotation on the page is removed.
      </NPopconfirm>
      <NButton :disabled="acting" data-testid="lint-edit" @click="onEdit">Edit</NButton>
      <NPopconfirm
        v-if="hasPatch"
        placement="top-end"
        :width="POPCONFIRM_WIDTH"
        positive-text="Accept patch"
        negative-text="Cancel"
        @positive-click="onAccept"
      >
        <template #trigger>
          <NButton type="primary" :disabled="acting" data-testid="lint-accept">Accept</NButton>
        </template>
        Apply this patch to the page? The proposed patch is regenerated against the current page state right before
        applying.
      </NPopconfirm>
      <NButton v-else type="primary" :disabled="true" data-testid="lint-accept">Accept</NButton>
    </footer>
  </article>
</template>
